import React, { useMemo, useState, useEffect } from 'react';
import {
  Button,
  Drawer,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  LoadingButton,
} from '@one-thd/sui-atomic-components';
import { QuantityItem } from '@src/components/quantity-selection/QuantityItem';
import { ItemToReturn } from '@src/models/returns';
import { setUpdatedItem, useReturnStore } from '@src/stores/returnStore';

interface EditItemDrawerProps {
  displayDrawer: boolean;
  closeDrawer: () => void;
  item: ItemToReturn;
  onEditItemSubmit: (
    startingState: ItemToReturn,
    closeDrawer: () => void,
  ) => void;
  isPolicyValidatorLoading: boolean;
}

const EditItemDrawer = ({
  displayDrawer,
  closeDrawer,
  item,
  onEditItemSubmit,
  isPolicyValidatorLoading,
}: EditItemDrawerProps) => {
  const selectedItems = useReturnStore((state) => state.selectedItems);
  // starting state for review item drawer
  // Update starting state when onMount
  const [startingState, setStartingState] = useState<ItemToReturn>(item);
  const [hasWeightError, setHasWeightError] = useState<string | null>(null);
  // checks if item (which gets updated automatically) has changed from starting state
  const hasItemChanged = useMemo(() => {
    return JSON.stringify(item) !== JSON.stringify(startingState);
  }, [item, startingState]);

  const onCancelUpdate = () => {
    // revert to starting state if user cancels out of drawer
    setUpdatedItem(startingState);
    closeDrawer();
  };
  const onSubmitUpdate = () => {
    // Don't proceed if there's a weight error
    if (hasWeightError) return;
    // Don't proceed if quantity is less than 1
    if (item.returnQuantity < 1) return;
    onEditItemSubmit(startingState, closeDrawer);
  };

  useEffect(() => {
    if (displayDrawer) {
      setHasWeightError(null);
      setStartingState(item);
    }
  }, [displayDrawer]);

  return (
    <Drawer
      open={displayDrawer}
      onClose={onCancelUpdate}
      position="right"
      className="drawer-z-index-fix"
    >
      <DrawerHeader title="Edit Return Item" onClose={onCancelUpdate} />
      <DrawerBody>
        <QuantityItem
          item={item}
          selectedItems={selectedItems}
          isHrtvIneligible={false}
          setHasWeightError={setHasWeightError}
          hasWeightError={hasWeightError}
        />
      </DrawerBody>
      <DrawerFooter className="sui-bg-primary sui-p-4 sui-relative sui-bottom-0 sui-sticky sui-shadow-sm">
        <div className="sui-flex sui-gap-4 sui-flex-col">
          <LoadingButton
            fullWidth
            loading={isPolicyValidatorLoading}
            onClick={onSubmitUpdate}
            disabled={!hasItemChanged}
          >
            Update Return item
          </LoadingButton>
          <Button fullWidth variant="secondary" onClick={onCancelUpdate}>
            Cancel
          </Button>
        </div>
      </DrawerFooter>
    </Drawer>
  );
};

export default EditItemDrawer;
